<template>
  <div
    v-if="status === STATUS.LOADING"
    id="stripe-reader-loading"
    class="flex items-center gap-x-2 animate-pulse"
  >
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
      <circle r="4" cx="5" cy="5" class="fill-status-available" />
    </svg>
    <span>Connecting to Card Reader...</span>
  </div>
  <div
    v-else-if="status === STATUS.LOADED"
    id="stripe-reader-loaded"
    class="flex items-center gap-x-2 relative z-20"
  >
    <template v-if="selectedReader">
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" id="card-reader-circle-svg">
        <circle
          r="4"
          cx="5"
          cy="5"
          :class="isSelectedReaderOnline ? 'fill-status-seated' : 'fill-status-noshow'"
        />
      </svg>
      <div
        id="card-reader-status"
        :class="!isSelectedReaderOnline ? 'text-fg-error' : 'text-fg-primary'"
      >
        <span v-if="isSelectedReaderOnline">Connected to </span>
        <div
          class="relative inline cursor-pointer text-fg-link-primary"
          @click="isReaderListOpen = !isReaderListOpen"
        >
          <span id="card-reader-name">{{ selectedReader.label }}</span>
          <OnClickOutside
            @trigger="isReaderListOpen = false"
            id="stripe-reader-list"
            v-show="isReaderListOpen"
            class="absolute top-0 left-0 mt-6 bg-surface-primary rounded-md shadow-md w-60 py-2 space-y-1"
          >
            <div
              v-for="reader in terminalReaders"
              :key="reader.id"
              class="reader cursor-pointer px-4"
              :class="
                selectedReader.id === reader.id
                  ? 'text-fg-primary font-semibold'
                  : reader.status === 'offline'
                  ? 'text-fg-error'
                  : 'text-fg-link-primary'
              "
              @click="setSelectedReaderId(reader.id)"
            >
              <span>
                {{ reader.label }} <span v-if="reader.status === 'offline'">(Offline) </span>
              </span>
            </div>
          </OnClickOutside>
        </div>
        Card Reader <span v-if="!isSelectedReaderOnline">is offline</span>
      </div>
      <ArrowPathIcon
        id="refresh-reader-icon"
        class="w-4 h-4 cursor-pointer"
        @click="refreshReaders"
      />
    </template>
    <template v-else>
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
        <circle r="4" cx="5" cy="5" class="fill-status-noshow" />
      </svg>
      <span>
        Not connected to Card Reader.
        <span id="connect-btn" class="text-fg-link-primary cursor-pointer" @click="connectToReader">
          Connect
        </span>
      </span>
    </template>
  </div>
  <div v-else-if="status === STATUS.ERROR_LOADING" id="stripe-reader-error">
    <ErrorDisplay v-if="ui.error.message" v-bind="ui.error" />
    <span id="connect-btn" class="text-fg-link-primary cursor-pointer" @click="connectToReader">
      Try Again
    </span>
  </div>
</template>

<script lang="ts">
import { OnClickOutside } from '@vueuse/components'
import { mapActions, mapState, mapStores } from 'pinia'
import { defineComponent } from 'vue'

import ArrowPathIcon from '@/assets/arrowPath.svg?component'
import ErrorDisplay from '@/common/components/atoms/ErrorDisplay.vue'
import { useTerminalStore } from '@/stores/terminal'
import { blankError, displayHandled, setError } from '@/utils/errors'

import { StripeReaderRead } from '@generated/types'

const STATUS = {
  LOADING: 'loading',
  LOADED: 'loaded',
  ERROR_LOADING: 'error-loading',
}

export default defineComponent({
  name: 'StripeReaderSelect',
  components: { ErrorDisplay, OnClickOutside, ArrowPathIcon },
  data() {
    return {
      STATUS,
      status: STATUS.LOADING as (typeof STATUS)[keyof typeof STATUS],
      isReaderListOpen: false,
      ui: { error: blankError() },
    }
  },
  async mounted() {
    try {
      await this.terminalStore.fetchReaders()
      this.status = STATUS.LOADED
    } catch (error) {
      displayHandled(error, this.ui.error)
      this.status = STATUS.ERROR_LOADING
    }
  },
  methods: {
    ...mapActions(useTerminalStore, ['setSelectedReaderId']),
    async connectToReader(): Promise<void> {
      try {
        this.status = STATUS.LOADING
        await this.terminalStore.fetchReaders()
        if (this.terminalReaders?.length) {
          this.setSelectedReaderId(this.terminalReaders[0].id)
          this.status = STATUS.LOADED
        } else {
          setError(this.ui.error, {
            message: 'No card readers found.',
            code: 'NO_CARD_READERS_FOUND',
          })
          this.status = STATUS.ERROR_LOADING
        }
      } catch (error) {
        displayHandled(error, this.ui.error)
        this.status = STATUS.ERROR_LOADING
      }
    },
    async refreshReaders(): Promise<void> {
      try {
        this.status = STATUS.LOADING
        await this.terminalStore.fetchReaders()
        this.status = STATUS.LOADED
      } catch (error) {
        displayHandled(error, this.ui.error)
        this.status = STATUS.ERROR_LOADING
      }
    },
  },
  computed: {
    ...mapStores(useTerminalStore),
    ...mapState(useTerminalStore, ['selectedReader']),
    terminalReaders(): StripeReaderRead[] {
      return this.terminalStore.readers
    },
    isSelectedReaderOnline(): boolean {
      if (!this.selectedReader) return false
      return this.selectedReader.status === 'online'
    },
  },
  watch: {
    async isReaderListOpen(value: boolean): Promise<void> {
      if (!value) return
      await this.terminalStore.fetchReaders()
    },
  },
})
</script>
